import {identity, noop} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {useSelectedConversationsDetails} from '../../../../libs/inboxes/business/src/cells/conversations';
import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {isTeamNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {postAsync} from '../../../../libs/shared/core/src/services/http/http';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ToolbarButton} from '../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  ConversationStatusesEnum,
  findConversationContextNamespace,
  makeFindConversationActionContext,
} from '../../../helpers/models/conversationHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {setRecipientIsBlockedAsyncAction} from '../../../store/data/conversations/setRecipientIsBlockedAction';
import {setConversationsStatusAction} from '../../../store/data/conversations/statusConversationsActions';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {InboxesVisualConversationSelection} from '../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {removePanelAction} from '../../../store/panels/panelsActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {
  BlockRecipientConfirmationDialog,
  ReportPhishingConfirmationDialog,
} from '../viewer/conversation/actions/more/conversationsMoreCoordinator';
import {ConversationsStarCoordinatorContainer} from '../viewer/conversation/actions/star/conversationsStarCoordinatorContainer';
import {DestroyConfirmationDialog} from '../viewer/conversation/topbar/conversationTopbarDestroy';
import {InboxesTopbarMainArchive} from './inboxesTopbarMainArchive';
import {InboxesTopbarMainSnooze} from './inboxesTopbarMainSnooze';
import {InboxesTopbarMainSpam} from './inboxesTopbarMainSpam';
import {InboxesTopbarMainTrash} from './inboxesTopbarMainTrash';
import {InboxesTopbarSnooze} from './inboxesTopbarSnooze';
import {InboxesTopbarSpam} from './inboxesTopbarSpam';
import {InboxesTopbarTicketStatus} from './inboxesTopbarTicketStatus';
import {InboxesTopbarTrash} from './inboxesTopbarTrash';

/*
 * Props.
 */

interface InboxesTopbarActionsProps {
  cellsContext: CellsContext;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversationSelection: InboxesVisualConversationSelection;
  inboxesParameters: InboxesParameters;
  panelId: ViewerPanelId;
  areAllArchived: boolean;
  areAllTrashed: boolean;
  areAllSpam: boolean;
  areAllInternal: boolean;
  recipient: VisualRecipientModel | undefined;
  contextNamespaces: ReadonlyArray<string>;
  timeFormat: TimeFormatsEnum;
  reminderForAll: ReminderModel | undefined;
  remindersForAll: ReadonlyArray<ReminderModel>;
  isBulkActionRequired: boolean;
  isInPopup: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  grid-area: actions;
  margin-left: 20px;
  margin-right: 14px;

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(30px, auto);
  grid-column-gap: 8px;
  align-items: center;
`;

/*
 * Component.
 */

export const InboxesTopbarActions: FC<InboxesTopbarActionsProps> = (props) => {
  const dispatch = useAppDispatch();
  const {onlyConversation, conversations, isBulkActionRequired, cellsContext, panelId} = props;
  const [isDestroyDialogOpen, setDestroyDialogOpen] = useState(false);
  const [isBlockRecipientConfirmationOpen, setIsBlockRecipientConfirmationOpen] = useState(false);
  const [isReportPhishingConfirmationOpen, setIsReportPhishingConfirmationOpen] = useState(false);
  const userNamespace = useAppSelector(getUserNamespace);
  const accessibleConversationInboxes = props.conversationsInboxes.filter(
    (i) => i.namespace === userNamespace || isTeamNamespace(i.namespace),
  );

  const {areAllTicketingActionsEnabled} = useSelectedConversationsDetails(props);

  const onPopinClick = useEventHandler(() => {
    if (!onlyConversation) {
      return;
    }

    dispatch(removePanelAction(panelId));
    dispatch(navigateToLinkAction(onlyConversation.link));
  });

  const onDestroyConfirmClick = () => {
    if (isBulkActionRequired) {
      return;
    }

    const conversationIds = conversations.map((c) => c.id);
    dispatch(
      setConversationsStatusAction(
        props.cellsContext,
        conversationIds,
        ConversationStatusesEnum.DESTROYED,
        makeFindConversationActionContext(cellsContext, conversations),
        {
          preventUndo: true,
        },
      ),
    );
  };

  const onDestroyClick = !isBulkActionRequired ? () => setDestroyDialogOpen(true) : undefined;

  const onReportPhishingConfirmClick = () => {
    setIsReportPhishingConfirmationOpen(false);

    if (!onlyConversation) {
      return;
    }

    postAsync(`${onlyConversation.url}/report/phishing`);
  };

  const onReportPhishingClick = onlyConversation
    ? () => setIsReportPhishingConfirmationOpen(true)
    : undefined;

  const onBlockRecipientConfirmClick = () => {
    setIsBlockRecipientConfirmationOpen(false);

    if (!onlyConversation) {
      return;
    }

    // Block recipient in the current namespace
    const namespace = findConversationContextNamespace(props.cellsContext, onlyConversation);
    dispatch(setRecipientIsBlockedAsyncAction(onlyConversation.id, true, namespace));
    dispatch(
      setConversationsStatusAction(
        props.cellsContext,
        [onlyConversation.id],
        ConversationStatusesEnum.SPAM,
        makeFindConversationActionContext(cellsContext, conversations),
      ),
    );
  };

  const onBlockRecipientClick = onlyConversation
    ? () => setIsBlockRecipientConfirmationOpen(true)
    : undefined;

  const unblockRecipient = () => {
    if (!onlyConversation) {
      return;
    }

    // Unblock recipient in the current namespace
    const namespace = findConversationContextNamespace(props.cellsContext, onlyConversation);
    dispatch(setRecipientIsBlockedAsyncAction(onlyConversation.id, false, namespace));
    dispatch(
      setConversationsStatusAction(
        props.cellsContext,
        [onlyConversation.id],
        ConversationStatusesEnum.OPEN,
        makeFindConversationActionContext(cellsContext, conversations),
      ),
    );
  };

  const onUnblockRecipientClick = onlyConversation ? unblockRecipient : undefined;
  const areAllSnoozed = props.areAllArchived && props.remindersForAll.length > 0;

  const renderActions = () => {
    // "Snooze" as a main triage action only makes sense in email mode.
    if (areAllSnoozed && !areAllTicketingActionsEnabled) {
      return (
        <>
          <InboxesTopbarTrash
            cellsContext={props.cellsContext}
            panelId={props.panelId}
            conversations={props.conversations}
            conversationsInboxes={accessibleConversationInboxes}
            inboxesParameters={props.inboxesParameters}
            isBulkActionRequired={props.isBulkActionRequired}
          />
          {!props.areAllInternal && (
            <InboxesTopbarSpam
              cellsContext={props.cellsContext}
              panelId={props.panelId}
              conversations={props.conversations}
              conversationsInboxes={accessibleConversationInboxes}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
              contextNamespaces={props.contextNamespaces}
              areAllInternal={props.areAllInternal}
              recipient={props.recipient}
              onReportPhishingClick={onReportPhishingClick}
              onBlockRecipientClick={onBlockRecipientClick}
              onUnblockRecipientClick={onUnblockRecipientClick}
            />
          )}
          <InboxesTopbarMainSnooze
            panelId={props.panelId}
            cellsContext={props.cellsContext}
            inboxesParameters={props.inboxesParameters}
            conversationsInboxes={accessibleConversationInboxes}
            conversations={props.conversations}
            remindersForAll={props.remindersForAll}
            timeFormat={props.timeFormat}
            contextNamespaces={props.contextNamespaces}
            isBulkActionRequired={props.isBulkActionRequired}
          />
        </>
      );
    }

    if (props.areAllSpam) {
      return (
        <InboxesTopbarMainSpam
          cellsContext={props.cellsContext}
          panelId={props.panelId}
          conversations={props.conversations}
          conversationsInboxes={accessibleConversationInboxes}
          inboxesParameters={props.inboxesParameters}
          isBulkActionRequired={props.isBulkActionRequired}
          recipient={props.recipient}
          areAllInternal={props.areAllInternal}
          contextNamespaces={props.contextNamespaces}
          onReportPhishingClick={onReportPhishingClick}
          onBlockRecipientClick={onBlockRecipientClick}
          onUnblockRecipientClick={onUnblockRecipientClick}
          onDestroyClick={onDestroyClick}
        />
      );
    }

    if (props.areAllTrashed) {
      return (
        <>
          {!props.areAllSpam && (
            <InboxesTopbarSpam
              cellsContext={props.cellsContext}
              panelId={props.panelId}
              conversations={props.conversations}
              conversationsInboxes={accessibleConversationInboxes}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
              contextNamespaces={props.contextNamespaces}
              areAllInternal={props.areAllInternal}
              recipient={props.recipient}
              onReportPhishingClick={onReportPhishingClick}
              onBlockRecipientClick={onBlockRecipientClick}
              onUnblockRecipientClick={onUnblockRecipientClick}
            />
          )}
          <InboxesTopbarMainTrash
            cellsContext={props.cellsContext}
            panelId={props.panelId}
            conversations={props.conversations}
            conversationsInboxes={accessibleConversationInboxes}
            inboxesParameters={props.inboxesParameters}
            isBulkActionRequired={props.isBulkActionRequired}
          />
        </>
      );
    }

    return (
      <>
        <InboxesTopbarSnooze
          panelId={props.panelId}
          cellsContext={props.cellsContext}
          inboxesParameters={props.inboxesParameters}
          conversationsInboxes={accessibleConversationInboxes}
          conversations={props.conversations}
          areAllTicketingEnabled={areAllTicketingActionsEnabled}
          reminderForAll={props.reminderForAll}
          timeFormat={props.timeFormat}
          contextNamespaces={props.contextNamespaces}
          isBulkActionRequired={props.isBulkActionRequired}
        />
        <InboxesTopbarTrash
          cellsContext={props.cellsContext}
          panelId={props.panelId}
          conversations={props.conversations}
          conversationsInboxes={accessibleConversationInboxes}
          inboxesParameters={props.inboxesParameters}
          isBulkActionRequired={props.isBulkActionRequired}
        />
        {!props.areAllInternal && (
          <InboxesTopbarSpam
            cellsContext={props.cellsContext}
            panelId={props.panelId}
            conversations={props.conversations}
            conversationsInboxes={accessibleConversationInboxes}
            inboxesParameters={props.inboxesParameters}
            isBulkActionRequired={props.isBulkActionRequired}
            contextNamespaces={props.contextNamespaces}
            areAllInternal={props.areAllInternal}
            recipient={props.recipient}
            onReportPhishingClick={onReportPhishingClick}
            onBlockRecipientClick={onBlockRecipientClick}
            onUnblockRecipientClick={onUnblockRecipientClick}
          />
        )}
        {areAllTicketingActionsEnabled && props.conversationSelection.isFullyKnown ? (
          <InboxesTopbarTicketStatus cellsContext={props.cellsContext} conversations={conversations} />
        ) : (
          <InboxesTopbarMainArchive
            cellsContext={props.cellsContext}
            panelId={props.panelId}
            conversations={props.conversations}
            conversationsInboxes={accessibleConversationInboxes}
            inboxesParameters={props.inboxesParameters}
            isBulkActionRequired={props.isBulkActionRequired}
          />
        )}
      </>
    );
  };

  if (conversations.length === 0) {
    return null;
  }

  const renderPhishingDialog = () => (
    <ReportPhishingConfirmationDialog
      onOkClick={onReportPhishingConfirmClick}
      onRequestClose={() => setIsReportPhishingConfirmationOpen(false)}
    />
  );

  const renderBlockRecipientDialog = () => {
    if (!props.recipient) {
      return null;
    }

    const namespace = props.onlyConversation
      ? findConversationContextNamespace(props.cellsContext, props.onlyConversation)
      : undefined;

    return (
      <BlockRecipientConfirmationDialog
        onOkClick={onBlockRecipientConfirmClick}
        onRequestClose={() => setIsBlockRecipientConfirmationOpen(false)}
        recipient={props.recipient}
        namespace={namespace}
      />
    );
  };

  const renderDestroyDialog = () => (
    <DestroyConfirmationDialog
      conversations={props.conversations}
      onDestroyClick={onDestroyConfirmClick}
      onRequestClose={() => setDestroyDialogOpen(false)}
      wrap={identity}
    />
  );

  return (
    <ConversationsStarCoordinatorContainer
      cellsContext={props.cellsContext}
      inboxesParameters={props.inboxesParameters}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      panelId={props.panelId}
      renderButton={(starProps) => (
        <KeyboardShortcuts
          handlers={{
            [KeyboardActionsEnum.STAR]: starProps.permissionResult.isAllowed ? starProps.onClick : noop,
          }}
        >
          <StyledWrapperDiv>
            {renderActions()}
            {maybeRenderPopin(props, onPopinClick)}
            {isDestroyDialogOpen && renderDestroyDialog()}
            {isReportPhishingConfirmationOpen && renderPhishingDialog()}
            {isBlockRecipientConfirmationOpen && renderBlockRecipientDialog()}
          </StyledWrapperDiv>
        </KeyboardShortcuts>
      )}
    />
  );
};

/*
 * Pop-in.
 */

const StyledPopinWrapperDiv = styled.div`
  margin-left: 2px;
  border-left: 1px solid ${(p) => p.theme.greys.shade40};
  padding-left: 8px;
`;

function maybeRenderPopin(props: InboxesTopbarActionsProps, onPopinClick: () => void) {
  const {isInPopup, onlyConversation} = props;
  if (!isInPopup || !onlyConversation || !onPopinClick) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledPopinWrapperDiv>
          <ToolbarButton
            iconName="popin"
            onClick={onPopinClick}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            label={<Trans id="toolbar-popin">View in main window</Trans>}
          />
        </StyledPopinWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
